/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { sortByProp } from '@squiz/dxp-console-support';
import { SelectField, SelectFieldOption } from '@squiz/sds';
import React, { Ref, forwardRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CORE_STRINGS } from '../../../../Constants/Strings.constants';
import { EntityTenantSettingsSchemaListItem } from '../../../../Services/tenant-settings';
import { SCHEMA_SELECT_DATA_IDS, SCHEMA_SELECT_FORM_IDS } from './SelectSchema.constants';
import { SelectSchemaProps } from './SelectSchema.props';

/**
 * Select field for Schemas to insert into forms.
 */
export const SelectSchema = forwardRef((props: SelectSchemaProps, ref: Ref<HTMLInputElement>): JSX.Element => {
  const { t } = useTranslation();
  const { callback, errorMessage, schemas, preSelectedSchema, uiAccess } = props;

  const labels = useMemo(() => {
    return {
      placeholder: t(CORE_STRINGS.schemaSelectPlaceholder),
      schema: t(CORE_STRINGS.schema),
    };
  }, [t]);

  const selectOptions = schemas
    .filter((schema: EntityTenantSettingsSchemaListItem) => {
      const hasUiAccess = schema.uiOptions[uiAccess];
      return hasUiAccess;
    })
    .map((schema: EntityTenantSettingsSchemaListItem): SelectFieldOption => {
      const { displayName, schemaId } = schema;

      return {
        key: schemaId,
        label: displayName,
        value: schemaId,
      };
    })
    .sort((a, b) => sortByProp(a, b, (item) => item.label));

  // Use pre-selected schema as default if it's provided.
  let defaultSelection: string | undefined;
  let defaultOption = undefined;

  if (preSelectedSchema) {
    defaultSelection = preSelectedSchema.schemaId;
    defaultOption = {
      key: preSelectedSchema.schemaId,
      label: preSelectedSchema.displayName,
      value: preSelectedSchema.schemaId,
    } as SelectFieldOption;
  }

  const [selectedSchema, setSelectedSchema] = useState<string | undefined>(defaultSelection);
  const handleSchemaChange = (option: SelectFieldOption): void => {
    setSelectedSchema(option.value);
    callback && callback();
  };

  return (
    <SelectField
      callback={handleSchemaChange}
      data-id={SCHEMA_SELECT_DATA_IDS.schema}
      defaultOption={defaultOption}
      errorMessage={errorMessage}
      fieldLabel={labels.schema}
      id={SCHEMA_SELECT_FORM_IDS.schema}
      name={SCHEMA_SELECT_FORM_IDS.schema}
      options={selectOptions}
      placeholder={labels.placeholder}
      ref={ref}
      required
      value={selectedSchema}
    />
  );
});
